import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { Modal } from '../../../../../molecules/Modal'
import { cardisTheme } from '../../theme/cardisTheme'
import { CardisBrokerData } from '../../types'

export interface BrokerCandidate {
  id: string
  name: string
  email: string
  phone: string
  photoUrl?: string
  office?: string
}

interface BrokerPickerModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (broker: CardisBrokerData) => void
  candidates: BrokerCandidate[]
  loading?: boolean
  /** ID of the primary broker to exclude from the list */
  excludeBrokerId?: string
}

const StyledModal = styled(Modal)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`

const ModalContent = styled.div`
  padding: 20px;
  width: 360px;
  box-sizing: border-box;
`

const ModalTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 15px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
`

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${cardisTheme.colors.border};
  border-radius: 6px;
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  outline: none;
  box-sizing: border-box;
  margin-bottom: 8px;

  &:focus {
    border-color: ${cardisTheme.colors.primary};
  }

  &::placeholder {
    color: ${cardisTheme.colors.textMuted};
  }
`

const CandidateList = styled.div`
  max-height: 280px;
  overflow-y: auto;
`

const CandidateRow = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px;
  border: none;
  border-radius: 6px;
  background: none;
  cursor: pointer;
  text-align: left;
  font-family: ${cardisTheme.fonts.body};
  transition: background 0.15s ease;

  &:hover {
    background: ${cardisTheme.colors.backgroundLight};
  }
`

const CandidatePhoto = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`

const CandidatePhotoPlaceholder = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${cardisTheme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
`

const CandidateInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const CandidateName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
`

const CandidateDetail = styled.div`
  font-size: 10px;
  color: ${cardisTheme.colors.textLight};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const EmptyState = styled.div`
  padding: 20px;
  text-align: center;
  font-size: 12px;
  color: ${cardisTheme.colors.textMuted};
`

const getInitials = (name: string) => {
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

export const BrokerPickerModal: React.FC<BrokerPickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  candidates,
  loading,
  excludeBrokerId,
}) => {
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const available = excludeBrokerId
      ? candidates.filter((c) => c.id !== excludeBrokerId)
      : candidates
    if (!search) return available
    const query = search.toLowerCase()
    return available.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.email.toLowerCase().includes(query)
    )
  }, [candidates, search, excludeBrokerId])

  const handleSelect = (candidate: BrokerCandidate) => {
    onSelect({
      name: candidate.name,
      email: candidate.email,
      phone: candidate.phone,
      photoUrl: candidate.photoUrl,
    })
    setSearch('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <StyledModal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalTitle>Ajouter un courtier</ModalTitle>
        <SearchInput
          placeholder="Rechercher un courtier..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
        <CandidateList>
          {loading ? (
            <EmptyState>Chargement...</EmptyState>
          ) : filtered.length === 0 ? (
            <EmptyState>Aucun résultat</EmptyState>
          ) : (
            filtered.map((candidate) => (
              <CandidateRow
                key={candidate.id}
                onClick={() => handleSelect(candidate)}
              >
                {candidate.photoUrl ? (
                  <CandidatePhoto
                    src={candidate.photoUrl}
                    alt={candidate.name}
                  />
                ) : (
                  <CandidatePhotoPlaceholder>
                    {getInitials(candidate.name)}
                  </CandidatePhotoPlaceholder>
                )}
                <CandidateInfo>
                  <CandidateName>{candidate.name}</CandidateName>
                  <CandidateDetail>
                    {candidate.email}
                    {candidate.office && ` — ${candidate.office}`}
                  </CandidateDetail>
                </CandidateInfo>
              </CandidateRow>
            ))
          )}
        </CandidateList>
      </ModalContent>
    </StyledModal>
  )
}
